/**
 * Shared client-side DOMPurify allow-list for Rich_Help_Text
 * (application-builder-rich-help-text spec, task 7.1).
 *
 * The server sanitizes authored help-text HTML down to a fixed nine-tag
 * allow-list before persistence (the authoritative pass, Req 3.1–3.3). This
 * config re-applies the SAME allow-list on the client at render time so that
 * even a corrupted or legacy stored value can never inject active markup — the
 * defense-in-depth half of the belt-and-suspenders sanitization boundary
 * (Req 4.3, property P8.1).
 *
 * It mirrors the established DOMPurify allow-list precedent in
 * `frontend/src/components/messages/stream/slack/renderMarkdown.ts`, but is
 * deliberately narrower: help text is formatting-only, so there are NO
 * attributes (`ALLOWED_ATTR: []`) — which means no `on*=` handlers, no `style`,
 * no `href` — and no URL-bearing elements (`<a>` is forbidden), so there is no
 * URL scheme to allow-list.
 *
 * The nine allow-listed tags match `HELP_TEXT_ALLOWED_TAGS` on the server
 * (`infrastructure/lambda/application-form/helpTextSanitize.ts`): paragraphs,
 * line breaks, bold, italic, and ordered + unordered lists.
 */
import type { Config } from 'dompurify';

/**
 * The fixed nine formatting tags allowed in Rich_Help_Text. Kept in sync with
 * the server-side `HELP_TEXT_ALLOWED_TAGS` so both sanitization boundaries agree
 * (Req 3.2, 4.3).
 */
export const HELP_TEXT_ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'ul',
  'ol',
  'li',
] as const;

/**
 * DOMPurify configuration for the client re-sanitize pass. No attributes are
 * permitted, and the URL-bearing / active-content tags are explicitly forbidden
 * so their content is dropped rather than leaking into the rendered DOM.
 */
export const HELP_TEXT_PURIFY_CONFIG: Config = {
  ALLOWED_TAGS: [...HELP_TEXT_ALLOWED_TAGS],
  ALLOWED_ATTR: [],
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'a'],
  // Explicitly return a plain string so TypeScript does not narrow to
  // TrustedHTML on environments where Trusted Types are declared (matching the
  // renderMarkdown.ts precedent).
  RETURN_TRUSTED_TYPE: false as const,
};

/**
 * The client-side plain-text projection of help-text HTML — the mirror of the
 * server's `htmlToPlainText` (`infrastructure/lambda/application-form/helpTextSanitize.ts`).
 *
 * When a Director disables Formatting_Opt_In on a surface that held
 * Rich_Help_Text, the builder retains the authored text as PLAIN by copying this
 * projection into the plain `helpText` input (application-builder-rich-help-text
 * Req 1.5). It strips all tags and collapses runs of whitespace to single
 * spaces, inserting a separator before every tag start so adjacent block/list
 * boundaries (`</li><li>`, `</p><p>`, `<br>`) do not run their text together.
 *
 * Sanitization is applied first (defense-in-depth) so this is safe on any input,
 * and because stripping happens on the sanitized string, no active markup can
 * leak into the resulting text.
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  return (
    html
      // Block and line-break boundaries become spaces so words never fuse.
      .replace(/<\s*br\s*\/?\s*>/gi, ' ')
      .replace(/<\/\s*(p|li|ul|ol)\s*>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      // Decode the entities the allow-listed markup can carry.
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      // Collapse whitespace and trim.
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Escape a plain string into a single allow-listed `<p>` paragraph so a Director
 * who enables Formatting_Opt_In on a surface that only had Plain_Help_Text keeps
 * their authored text visible in the Rich_Text_Editor rather than starting empty.
 * The escape prevents any stray angle brackets in the plain text from being
 * interpreted as markup (the server still sanitizes authoritatively).
 */
export function plainTextToHelpHtml(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length === 0) return '';
  const escaped = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped}</p>`;
}
